import { useState, useEffect, useCallback } from 'react';

import { getParticipants } from '../api';
import { Participant, CallWithDuration } from '../types';

type UseParticipantsResult = {
  participants: Participant[];
  participantsError: string | null;
};

const useParticipants = (
  call: CallWithDuration | undefined,
  status: string | undefined,
): UseParticipantsResult => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsError, setParticipantsError] = useState<string | null>(null);

  const fetchParticipants = useCallback(async() => {
    if (!call?.parameters?.CallSid) return;

    try {
      const response = await getParticipants(call.parameters.CallSid);
      const fetchedParticipants = response.participants || [];
      setParticipants(fetchedParticipants);
      call.emit('hold', response.on_hold);
      setParticipantsError(null);

      // Auto-close call if all participants have left
      if (
        fetchedParticipants.length > 0 &&
        fetchedParticipants.every(p => p.status === 'left')
      ) {
        console.info('All participants left - forcing disconnect');
        call.disconnect();
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      setParticipantsError('Failed to load participants');
    }
  }, [call]);

  useEffect(() => {
    setParticipants([]);
    setParticipantsError(null);

    if (!call) {
      return;
    }

    fetchParticipants();

    if (status === 'closed') {
      return;
    }

    // Poll every 3 seconds for faster hold status updates across multiple agents
    const interval = setInterval(fetchParticipants, 3000);

    return () => clearInterval(interval);
  }, [call, status, fetchParticipants]);

  return { participants, participantsError };
};

export default useParticipants;
